import type { MoveCommand } from "@eldrun/sim";

/** Mapeamento padrão de teclas (remapeável na F30). */
export const KEYS = {
  forward: ["KeyW", "ArrowUp"],
  back: ["KeyS", "ArrowDown"],
  left: ["KeyA", "ArrowLeft"],
  right: ["KeyD", "ArrowRight"],
  jump: ["Space"],
  sprint: ["ShiftLeft", "ShiftRight"],
  // O C foi para a ficha (F82); agachar fica no Ctrl.
  crouch: ["ControlLeft"],
  inventory: ["Tab", "KeyI"],
  discard: ["KeyQ"],
  crafting: ["KeyF"],
  rotate: ["KeyR"],
  interact: ["KeyE"],
  dismantle: ["KeyX"],
  map: ["KeyM"],
  ping: ["KeyG", "MouseMiddle"],
  chat: ["Enter", "NumpadEnter"],
  settings: ["KeyP"],
  // Habilidades da classe (F65): a ativa no botão direito (ou Z), a ultimate no V.
  skill: ["MouseRight", "KeyZ"],
  ultimate: ["KeyV"],
  // Painel de Bênçãos e Habilidades (F70).
  abilities: ["KeyB"],
  // Ficha da classe (F82).
  sheet: ["KeyC"],
} as const;

/** Teclas de cada ação (as padrão, ou as remapeadas nas Opções). */
export type KeyBindings = { [A in keyof typeof KEYS]: readonly string[] };

/** Teclas que o navegador usaria para outra coisa (rolagem, foco, busca rápida). */
const BROWSER_KEYS = new Set(["Space", "Tab", "Slash", "Quote", "Backquote", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);

/** Foco num campo de texto (chat): as teclas são do campo, não do jogo. */
export function typingIn(target: EventTarget | null): boolean {
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) return false;
  return target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

/** Teclas 1–8 do cinto. */
export const BELT_KEYS = ["Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8"] as const;

const PITCH_LIMIT = 1.55;
export const DEFAULT_SENSITIVITY = (0.12 * Math.PI) / 180;

/** Estado de teclado e mouse → comando de movimento por passo fixo (puro de testar via `command`). */
export class PlayerInput {
  yaw = 0;
  pitch = 0;
  sensitivity = DEFAULT_SENSITIVITY;
  /** Inversão dos eixos do mouse (Opções). */
  invertX = false;
  invertY = false;
  /** Teclas de cada ação (Opções → Controles). */
  bindings: KeyBindings = KEYS;
  /** Tela de Opções aberta: o teclado é dela (só a tecla de Opções passa, para fechar). */
  paused = false;
  private readonly down = new Set<string>();
  private readonly pressed = new Set<string>();
  private wheel = 0;
  /** Ação principal (botão esquerdo com o mouse travado): mantida e toques. */
  private primaryHeld = false;
  private primaryClicks = 0;

  key(code: string, isDown: boolean): void {
    if (isDown && !this.down.has(code)) this.pressed.add(code);
    if (isDown) this.down.add(code);
    else this.down.delete(code);
  }

  look(dx: number, dy: number): void {
    this.yaw -= (this.invertX ? -dx : dx) * this.sensitivity;
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch - (this.invertY ? -dy : dy) * this.sensitivity));
    // Mantém o yaw em (−π, π] (a simulação também normaliza).
    this.yaw -= 2 * Math.PI * Math.floor((this.yaw + Math.PI) / (2 * Math.PI));
  }

  clear(): void {
    this.down.clear();
    this.pressed.clear();
    this.primaryHeld = false;
    this.primaryClicks = 0;
  }

  /** Botão de ação apertado ou solto (mouse, ou o gancho de teste). */
  primary(down: boolean): void {
    if (down && !this.primaryHeld) this.primaryClicks++;
    this.primaryHeld = down;
  }

  /** A ação principal está apertada? (segurar repete o golpe) */
  primaryDown(): boolean {
    return this.primaryHeld;
  }

  /** Houve clique de ação desde a última consulta? (coleta, posicionar) */
  consumePrimaryClick(): boolean {
    const clicked = this.primaryClicks > 0;
    this.primaryClicks = 0;
    return clicked;
  }

  isDown(action: keyof typeof KEYS): boolean {
    return this.bindings[action].some((k) => this.down.has(k));
  }

  /** Índice do cinto tocado (1–8 → 0–7) desde a última consulta, ou −1. */
  consumeBeltKey(): number {
    for (let i = 0; i < BELT_KEYS.length; i++) {
      const code = BELT_KEYS[i] as string;
      if (this.pressed.delete(code)) return i;
    }
    return -1;
  }

  /** Passos da roda do mouse acumulados (positivo = próximo). */
  consumeWheel(): number {
    const steps = this.wheel;
    this.wheel = 0;
    return steps;
  }

  shiftHeld(): boolean {
    return this.down.has("ShiftLeft") || this.down.has("ShiftRight");
  }

  /** Verdadeiro uma vez por toque (consumido). */
  consumePressed(action: keyof typeof KEYS): boolean {
    const hit = this.bindings[action].some((k) => this.pressed.has(k));
    for (const k of this.bindings[action]) this.pressed.delete(k);
    return hit;
  }

  command(): MoveCommand {
    const axis = (pos: keyof typeof KEYS, neg: keyof typeof KEYS) => (this.isDown(pos) ? 1 : 0) - (this.isDown(neg) ? 1 : 0);
    return {
      moveX: axis("right", "left"),
      moveZ: axis("forward", "back"),
      yaw: this.yaw,
      pitch: this.pitch,
      jump: this.isDown("jump"),
      sprint: this.isDown("sprint"),
      crouch: this.isDown("crouch"),
      use: this.primaryHeld,
      // A tecla da ativa também vai no movimento (F66): a Investida é predita; sem ela, nada acontece.
      dash: this.isDown("skill"),
      // E a da ultimate (F67): o Salto do Falcão também é predito.
      leap: this.isDown("ultimate"),
    };
  }

  /** Liga teclado e mouse (pointer lock) ao elemento do jogo. Devolve função para desligar. */
  attach(element: HTMLElement): () => void {
    const onKeyDown = (e: KeyboardEvent) => {
      // Digitando no chat: nada de andar, golpear ou abrir painéis (soltar a tecla continua valendo).
      if (typingIn(e.target)) return;
      if (this.paused && !this.bindings.settings.includes(e.code)) return;
      if (BROWSER_KEYS.has(e.code) && (e.code === "Space" || e.code === "Tab" || e.code.startsWith("Arrow") || Object.values(this.bindings).some((keys) => keys.includes(e.code)))) e.preventDefault();
      this.key(e.code, true);
    };
    const onWheel = (e: WheelEvent) => {
      if (document.pointerLockElement === element) this.wheel += Math.sign(e.deltaY);
    };
    const onKeyUp = (e: KeyboardEvent) => this.key(e.code, false);
    // O primeiro movimento depois de travar costuma trazer um salto enorme (F99): descartado, a câmera não gira.
    let skipLook = false;
    let locked = false;
    const onMouse = (e: MouseEvent) => {
      if (document.pointerLockElement !== element) return;
      if (skipLook) skipLook = false;
      else this.look(e.movementX, e.movementY);
    };
    // Já travado (o clique é golpe), não pede de novo: o pedido repetido pode soltar outro pointerlockchange.
    const onClick = () => {
      if (document.pointerLockElement !== element) void element.requestPointerLock?.();
    };
    const onBlur = () => this.clear();
    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 0 && document.pointerLockElement === element) this.primary(true);
      // Botão do meio marca um ping (sem a rolagem automática do navegador).
      if (e.button === 1 && (document.pointerLockElement === element || e.target === element)) {
        e.preventDefault();
        this.key("MouseMiddle", true);
      }
      // Botão direito: a habilidade ativa (F65), com o ponteiro preso.
      if (e.button === 2 && document.pointerLockElement === element) this.key("MouseRight", true);
    };
    const onMouseUp = (e: MouseEvent) => {
      if (e.button === 0) this.primary(false);
      if (e.button === 1) this.key("MouseMiddle", false);
      if (e.button === 2) this.key("MouseRight", false);
    };
    // Sem o menu de contexto do navegador sobre o jogo (o botão direito é a habilidade).
    const onContextMenu = (e: MouseEvent) => {
      if (document.pointerLockElement === element || e.target === element) e.preventDefault();
    };
    // Soltar o ponteiro (Esc, abrir painel) solta a ação: nada fica "preso" comendo. Travar descarta o próximo movimento.
    const onLockChange = () => {
      const now = document.pointerLockElement === element;
      // Só a passagem de solto para travado descarta o movimento (revisão da F99).
      if (now && !locked) skipLook = true;
      if (!now) {
        skipLook = false;
        this.primary(false);
      }
      locked = now;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    document.addEventListener("mousemove", onMouse);
    element.addEventListener("click", onClick);
    window.addEventListener("blur", onBlur);
    window.addEventListener("wheel", onWheel, { passive: true });
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("pointerlockchange", onLockChange);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("pointerlockchange", onLockChange);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("mousemove", onMouse);
      element.removeEventListener("click", onClick);
      window.removeEventListener("blur", onBlur);
    };
  }
}
